import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    deleteProcessingTask,
    getProcessingList,
    getProcessingListDetail,
    updateProcessingInputAPI,
    updateProcessingOutputAPI,
} from 'components/pages/processing/services';
import { getTaskCategoryOptionsFetcher } from 'libs/axios/base';
import moment from 'moment';
import { showAlert } from 'reducer/rootReducer';
import { RootState } from 'store';
import { ALl_STATUS, FORMAT_DATETIME, PAGINATION } from 'utils/constants';
import { ALERT_TYPE } from 'utils/enums';
import { getLocalStorageValue } from 'utils/helpers';
import { ProcessStatus } from '../state.default';

export const fetchProcessingList = createAsyncThunk(
    'processingTask/fetchProcessingList',
    async (params: ProcessingSearch, { rejectWithValue, getState }) => {
        try {
            const processingTaskState = getState() as RootState;
            const contract_code = getLocalStorageValue('contract');

            const isValidValue = (value: any) =>
                value !== null && value !== undefined && value !== '';
            const isDateKey = (key: string) =>
                [
                    'planned_date_from',
                    'planned_date_to',
                    'processing_input_date_from',
                    'processing_input_date_to',
                    'processing_input_done_date_from',
                    'processing_input_done_date_to',
                    'processing_done_date_from',
                    'processing_done_date_to',
                ].includes(key);

            const formatDateValue = (value: any) =>
                moment.utc(value).format(FORMAT_DATETIME.YYYYMMDD2);

            const filter =
                params &&
                Object.entries(params).reduce<ProcessingSearch>((acc, [key, value]) => {
                    if (key === 'status' && value) {
                        acc[key] = [value.toString()];
                    } else if (isValidValue(value) || key === 'delete_at') {
                        acc[key as keyof ProcessingSearch] = isDateKey(key)
                            ? formatDateValue(value)
                            : value;
                    }
                    return acc;
                }, {});
            const hasOtherKeys = Object.keys(filter).some((key) => key !== 'delete_at');
            const hasStatus = 'status' in filter;

            if (hasOtherKeys && !hasStatus) {
                filter.status = [...ALl_STATUS, `${ProcessStatus.Deleted}`];
            } else if (!filter.hasOwnProperty('status')) {
                filter.status = [
                    `${ProcessStatus.ProcessingStarted}`,
                    `${ProcessStatus.ProcessingSubmitted}`,
                ];
            }

            if (
                filter.status &&
                Array.isArray(filter.status) &&
                filter.status.length === 1 &&
                filter.status[0].includes(',')
            ) {
                filter.status = filter.status[0].split(',');
            }

            // const filterDispatch = {
            //     ...filter,
            //     status: filter.status,
            // } as ProcessingSearch;
            // dispatch(setFormSearch(filterDispatch || params));

            const request: ProcessingListRequest = {
                page: processingTaskState.processingTask.paginateMetaData.page,
                per_page: PAGINATION.LIMIT_PER_PAGE,
                filter: JSON.stringify({ ...filter, contract_code }),
            };

            if (processingTaskState?.processingTask?.sort) {
                request.sort = processingTaskState.processingTask.sort;
            }

            const res = await getProcessingList(request);

            if (!res.success) {
                return rejectWithValue(res.error);
            }

            return res.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

export const fetchProcessingListDetail = createAsyncThunk(
    'processingTask/fetchProcessingListDetail',
    async (task_id: string, { rejectWithValue }) => {
        try {
            const res = await getProcessingListDetail(task_id);
            if (!res.success) {
                return rejectWithValue(res.error);
            }
            const data: ProcessingTableDetail[] = res.data.data;
            return data;
        } catch (error) {
            rejectWithValue(error);
        }
    },
);

export const fetchDeleteProcessing = createAsyncThunk(
    'processingTask/fetchDeleteProcessing',
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState() as RootState;
            const { selectedItems, dataTable } = state.processingTask as ProcessingTaskState;

            const ids = selectedItems.map((index) => dataTable[index].task_id!);

            const res = await deleteProcessingTask(ids);

            if (!res.success) {
                return rejectWithValue(res.error);
            }

            dispatch(fetchProcessingList({} as ProcessingSearch));
            return res.message;
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

export const fetchTaskCategoryOptions = createAsyncThunk(
    'processingTask/fetchTaskCategoryOptions',
    async (page: number, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({
                page: `${page}`,
                per_page: `${PAGINATION.LIMIT_PER_PAGE}`,
                filter: JSON.stringify({ delete_at: null }),
            });

            const res = await getTaskCategoryOptionsFetcher(params);

            if (!res.success) {
                return rejectWithValue(res.error);
            }

            return res.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

export const updateProcessingOutput = createAsyncThunk(
    'processingTask/updateProcessingOutput',
    async (
        { task_id, request }: { task_id: string; request: IUpdateProcessingRequest },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await updateProcessingOutputAPI(task_id, request);

            if (!res.success) {
                dispatch(showAlert({ status: true, type: ALERT_TYPE.ERROR, message: res.error }));
                return rejectWithValue(res.error);
            }
            dispatch(showAlert({ status: true, type: ALERT_TYPE.SUCCESS, message: res.message }));
            return res.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

export const updateProcessingIntput = createAsyncThunk(
    'processingTask/updateProcessingInput',
    async (
        { task_id, request }: { task_id: string; request: IUpdateProcessingRequest },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const res = await updateProcessingInputAPI(task_id, request);

            if (!res.success) {
                dispatch(showAlert({ status: true, type: ALERT_TYPE.ERROR, message: res.error }));
                return rejectWithValue(res.error);
            }
            dispatch(showAlert({ status: true, type: ALERT_TYPE.SUCCESS, message: res.message }));
            return res.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);
